import { useEffect, useRef } from 'react'
import { queryKeys } from '@/hooks/query-keys'
import { notificationsApi } from '@/lib/api/notifications.api'
import { useEntityRealtime } from '@/lib/realtime'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export const NOTIFICATION_FEED_QUERY_KEY = queryKeys.notifications.feed()

/**
 * Lane-based notification feed (action required / mentions / activity).
 * Stays live via the existing notification socket events — created/updated/
 * deleted all trigger one debounced refetch — with a slow polling fallback.
 */
export function useNotificationFeed(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const queryClient = useQueryClient()
  const { addEventListener } = useEntityRealtime(
    'organization',
    organizationId ?? null,
    { suppressLogging: true },
  )

  const query = useQuery({
    queryKey: NOTIFICATION_FEED_QUERY_KEY,
    queryFn: () => notificationsApi.getFeed(),
    enabled: enabled && !!organizationId,
    staleTime: 20000,
    refetchInterval: 120000,
  })

  const invalidateTimeout = useRef<number | null>(null)
  useEffect(() => {
    if (!enabled || !organizationId) return

    const scheduleInvalidate = () => {
      if (invalidateTimeout.current !== null) return
      invalidateTimeout.current = window.setTimeout(() => {
        invalidateTimeout.current = null
        void queryClient.invalidateQueries({
          queryKey: NOTIFICATION_FEED_QUERY_KEY,
        })
      }, 800)
    }

    const cleanups = [
      addEventListener('NOTIFICATION_CREATED', scheduleInvalidate),
      addEventListener('NOTIFICATION_UPDATED', scheduleInvalidate),
      addEventListener('NOTIFICATION_DELETED', scheduleInvalidate),
      addEventListener('UNREAD_COUNT_UPDATED', scheduleInvalidate),
    ]
    return () => {
      cleanups.forEach(cleanup => cleanup())
      if (invalidateTimeout.current !== null) {
        window.clearTimeout(invalidateTimeout.current)
        invalidateTimeout.current = null
      }
    }
  }, [enabled, organizationId, addEventListener, queryClient])

  return query
}
